import * as React from "react";
import { useNotify } from "react-admin";
import {
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Button,
  Container,
  Box,
} from "@mui/material";
import { GdprBanner } from "./GdprBanner";
import { useLocation } from "react-router-dom";
import {
  InventoryTable,
  OrdersList,
  RemovalRequestDialog,
  SummaryCards,
} from "./partials";

const API_URL =
  process.env.REACT_APP_API_URL || "https://carebag.hubency.com/api";

interface InventoryRecord {
  id: string;
  airbagsCount: number;
  fullBinsCount: number;
  emptyBinsCount: number;
  createdAt: string;
  createdFrom: string;
}

interface OrderRecord {
  _id: string;
  orderNumber: string;
  quantityAirbag: number;
  quantityBin: number;
  status: string;
  provisionalDate: string;
}

interface OrderRespose {
  data: OrderRecord[];
}

interface ClientResponse {
  client: {
    _id: string;
    name: string;
    inventory: InventoryRecord;
  };
}

interface InventoryListResponse {
  data: InventoryRecord[];
  total: number;
}

export const ExternalsClientInventoryCreate: React.FC = () => {
  const notify = useNotify();
  const [client, setClient] = React.useState<{
    id: string;
    name: string;
  } | null>(null);
  const [inventory, setInventory] = React.useState<InventoryRecord[]>([]);
  const [latestInv, setLatestInv] = React.useState<InventoryRecord | null>(
    null
  );
  const [orders, setOrders] = React.useState<OrderRecord[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [total, setTotal] = React.useState(0);

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [certified, setCertified] = React.useState(false);

  const inviteToken = new URLSearchParams(useLocation().search).get(
    "inviteToken"
  )!;

  async function loadAll(inviteToken: string, rowsPerPage: number) {
    setLoading(true);
    try {
      const clRes = await fetch(
        `${API_URL}/client-inventory/invite/client?inviteToken=${inviteToken}`
      );
      if (!clRes.ok) throw new Error("Lien invalide");

      const { client: cl } = (await clRes.json()) as ClientResponse;
      setClient({ id: cl._id, name: cl.name });
      setLatestInv(cl.inventory);

      const invRes = await fetch(
        `${API_URL}/client-inventory/invite/list?inviteToken=${inviteToken}&page=1&limit=${rowsPerPage}`
      );
      if (!invRes.ok) throw new Error("Impossible de charger l'inventaire");
      const invJson = (await invRes.json()) as InventoryListResponse;
      setInventory(invJson.data);
      setTotal(invJson.total);

      const ordRes = await fetch(
        `${API_URL}/orders?inviteToken=${inviteToken}`
      );
      if (!ordRes.ok) throw new Error("Impossible de charger les commandes");
      const ordJson = (await ordRes.json()) as OrderRespose;

      setOrders(ordJson.data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => {
    loadAll(inviteToken, rowsPerPage);
  }, [inviteToken, rowsPerPage]);

  const handleSubmit = async (values: any) => {
    try {
      const res = await fetch(`${API_URL}/client-inventory/${client!.id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Invite-Token": inviteToken,
        },
        body: JSON.stringify(values),
      });
      if (!res.ok) throw new Error(await res.text());
      notify("Votre demande a été prise en compte", { type: "success" });
      setDialogOpen(false);
      setCertified(false);
      loadAll(inviteToken, 10);
      setPage(0);
      setRowsPerPage((r) => r);
    } catch (err: any) {
      notify(`Erreur : ${err.message}`, { type: "warning" });
    }
  };

  if (loading) return <CircularProgress sx={{ display: "block", m: "auto" }} />;
  if (error)
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );

  return (
    <>
      <title>Hubency Carebag - Inventaire client</title>
      <Container maxWidth="lg">
        <Typography variant="h5" gutterBottom align="center">
          Bonjour {client!.name}
        </Typography>
        <Typography
          variant="subtitle1"
          align="center"
          color="text.secondary"
          sx={{ mb: 3 }}
        >
          Bienvenue sur notre nouvelle plateforme de gestion, spécialement
          conçue pour simplifier et optimiser la gestion de vos airbags et
          caisses.
        </Typography>
        <SummaryCards latest={latestInv!} />

        <Box textAlign="center" sx={{ mt: 3, mb: 4 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setDialogOpen(true)}
          >
            Demander un enlèvement
          </Button>
        </Box>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <InventoryTable
              inventory={inventory}
              page={page}
              rowsPerPage={rowsPerPage}
              total={total}
              onPageChange={(p, r) => {
                setPage(p);
                setRowsPerPage(r);
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <OrdersList orders={orders} />
          </Grid>
        </Grid>
      </Container>

      <RemovalRequestDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmit}
        certified={certified}
        setCertified={setCertified}
        clientId={client!.id}
      />

      <GdprBanner />
    </>
  );
};
